import { Controller } from '@hotwired/stimulus';

// GLS Point-Auswahl (GLS Shop2Shop).
//
// Die PLZ der Rechnungsadresse ist die Vorauswahl fuer das Suchfeld. Wird sie
// geaendert, laeuft die Suche sofort; die Trefferliste wird per fetch
// aktualisiert, ohne die Seite neu zu laden - die bereits eingegebenen Daten
// der Rechnungsadresse bleiben dadurch erhalten.
//
// Ohne JavaScript schickt der "Suchen"-Button das ganze Formular ab
// (name="gls_search"); der Server speichert dann die Rechnungsadresse und
// rendert den Adress-Schritt mit den Treffern neu.
export default class extends Controller {
  static targets = ['query', 'results', 'plz', 'selected', 'searchButton', 'submit'];
  static values = { url: String, delay: { type: Number, default: 400 } };

  connect() {
    this.timer = null;
    this.requestId = 0;
    if (this.hasPlzTarget) this.plzTarget.addEventListener('input', this.onPlzInput);
    if (this.hasResultsTarget) this.resultsTarget.addEventListener('change', this.onSelectionChange);
    this.element.addEventListener('submit', this.onSubmit);
    this.element.addEventListener('keydown', this.onKeydown);
  }

  disconnect() {
    window.clearTimeout(this.timer);
    if (this.hasPlzTarget) this.plzTarget.removeEventListener('input', this.onPlzInput);
    if (this.hasResultsTarget) this.resultsTarget.removeEventListener('change', this.onSelectionChange);
    this.element.removeEventListener('submit', this.onSubmit);
    this.element.removeEventListener('keydown', this.onKeydown);
  }

  // Sobald ein GLS Point gewaehlt ist, ist der Hinweis "Bitte waehlen Sie einen
  // GLS Point aus" erledigt - er wird ausgeblendet.
  onSelectionChange = (event) => {
    const target = event.target;
    if (target.name !== 'gls_point_id') return;

    const hint = document.getElementById('gls-point-error');
    if (hint) hint.remove();
  };

  // PLZ der Rechnungsadresse geaendert: Suchfeld uebernehmen und sofort suchen.
  onPlzInput = () => {
    window.clearTimeout(this.timer);
    this.timer = window.setTimeout(() => {
      this.queryTarget.value = this.plzTarget.value;
      this.search();
    }, this.delayValue);
  };

  // "Suchen" abfangen und die Trefferliste per fetch nachladen. Alle anderen
  // Absendungen (z. B. "Weiter") laufen normal.
  onSubmit = (event) => {
    const submitter = event.submitter;
    if (!submitter || submitter.getAttribute('name') !== 'gls_search') return;

    event.preventDefault();
    // rails-ujs deaktiviert beim Absenden des Formulars alle Submit-Buttons
    // (auch wenn der Submit abgebrochen wird) - deshalb die Ausbreitung des
    // Submit-Ereignisses stoppen. Sonst bliebe der "Weiter"-Button nach einer
    // Suche deaktiviert.
    event.stopImmediatePropagation();
    this.search();
  };

  // Enter in einem Adressfeld soll den Checkout fortsetzen und nicht die Suche
  // ausloesen - der "Suchen"-Button steht weiter oben im Formular und waere
  // sonst der Default-Button der impliziten Formularabsendung.
  onKeydown = (event) => {
    if (event.key !== 'Enter' || event.shiftKey || event.altKey) return;

    const target = event.target;
    if (target === this.queryTarget) return;
    if (target.tagName === 'TEXTAREA' || target.tagName === 'BUTTON') return;
    if (target.type === 'submit' || target.type === 'button') return;
    if (!this.hasSubmitTarget) return;

    event.preventDefault();
    this.submitTarget.click();
  };

  // Suche ausfuehren und die Trefferliste ersetzen.
  search = () => {
    const url = new URL(this.urlValue, window.location.href);
    url.searchParams.set('q', this.queryTarget.value.trim());

    // Nummerierung der Anfragen: eine aeltere Antwort darf eine neuere nicht
    // ueberschreiben.
    const requestId = ++this.requestId;

    this.setBusy(true);
    fetch(url.toString(), {
      headers: { 'X-Requested-With': 'XMLHttpRequest', 'Accept': 'text/html' },
      credentials: 'same-origin'
    })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.text();
      })
      .then((html) => {
        if (requestId !== this.requestId) return;
        this.renderResults(html);
      })
      .catch((error) => console.error('GLS Point Suche fehlgeschlagen', error))
      .finally(() => {
        if (requestId === this.requestId) this.setBusy(false);
      });
  };

  renderResults(html) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const results = parsed.querySelector('#gls-point-results');
    if (!results || !this.hasResultsTarget) return;

    this.resultsTarget.innerHTML = results.innerHTML;
    this.resetStaleSelection();
  }

  // Ein zuvor gewaehlter Point, der nicht mehr in den Treffern ist, gilt als
  // nicht mehr gewaehlt.
  resetStaleSelection() {
    if (!this.hasSelectedTarget) return;

    const value = this.selectedTarget.value;
    if (!value) return;

    const stillListed = this.resultsTarget.querySelector(
      `input[name="gls_point_id"][value="${CSS.escape(value)}"]`
    );
    if (stillListed) return;

    this.selectedTarget.value = '';
    this.resultsTarget.querySelectorAll('.gls-point-selected').forEach((element) => element.remove());
  }

  setBusy(busy) {
    if (this.hasSearchButtonTarget) {
      this.searchButtonTarget.disabled = busy;
    }
  }
}
